import { 
    GrammarCard, 
    GrammarDatabase, 
    CategoriesDatabase, 
    LevelsDatabase,
    FilterOptions, 
    CardCategory, 
    GradeLevel,
    StudySession,
    UserProgress 
  } from '../types/grammar';
  
  export class GrammarCardService {
    private cardsDatabase: GrammarDatabase | null = null;
    private categoriesDatabase: CategoriesDatabase | null = null;
    private levelsDatabase: LevelsDatabase | null = null;
    private isLoaded = false;
    private cache = new Map<string, any>();
  
    async loadDatabase(): Promise<void> {
      if (this.isLoaded && this.cardsDatabase && this.categoriesDatabase && this.levelsDatabase) {
        return;
      }
  
      try {
        console.log('Loading grammar database...');
        
        // Load all databases in parallel for better performance
        const [cardsResponse, categoriesResponse, levelsResponse] = await Promise.all([
          this.fetchWithCache('/data/grammar/grammarCards.json'),
          this.fetchWithCache('/data/grammar/categories.json'),
          this.fetchWithCache('/data/grammar/levels.json')
        ]);
  
        if (!cardsResponse.ok || !categoriesResponse.ok || !levelsResponse.ok) {
          throw new Error('Failed to load one or more database files');
        }
  
        this.cardsDatabase = await cardsResponse.json();
        this.categoriesDatabase = await categoriesResponse.json();
        this.levelsDatabase = await levelsResponse.json();
        this.isLoaded = true;
  
        // FIX: Add null checks before accessing properties
        const cardCount = this.cardsDatabase?.cards?.length || 0;
        const categoryCount = this.categoriesDatabase?.categories?.length || 0;
        const levelCount = this.levelsDatabase?.levels?.length || 0;
  
        console.log(`✅ Loaded ${cardCount} grammar cards`);
        console.log(`✅ Loaded ${categoryCount} categories`);
        console.log(`✅ Loaded ${levelCount} grade levels`);
        
      } catch (error) {
        console.error('Failed to load grammar database:', error);
        this.loadFallbackData();
      }
    }
  
    private async fetchWithCache(url: string): Promise<Response> {
      const cacheKey = `fetch_${url}`;
      const cachedResponse = this.cache.get(cacheKey);
      
      if (cachedResponse) {
        return new Response(JSON.stringify(cachedResponse), { status: 200 });
      }
  
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.clone().json();
        this.cache.set(cacheKey, data);
      }
      
      return response;
    }
  
    private loadFallbackData(): void {
      console.warn('Loading fallback grammar data...');
      
      // Minimal fallback data to ensure app doesn't crash
      this.cardsDatabase = {
        version: "1.0.0-fallback",
        lastUpdated: new Date().toISOString(),
        metadata: {
          totalCards: 3,
          categories: ["parts_of_speech"],
          gradeLevels: [3, 6],
          difficulties: [1, 2]
        },
        cards: [
          {
            id: "fallback_001",
            question: "What is a noun?",
            options: [
              "A person, place, animal, quality, thing, action or idea.",
              "An action word.",
              "A describing word.",
              "A type of adverb."
            ],
            answer: "A person, place, animal, quality, thing, action or idea.",
            moreInfo: "Nouns tell us who's there, what's happening, and where it is.",
            difficulty: 1,
            gradeLevel: 3,
            category: "parts_of_speech",
            tags: ["basic"],
            dateCreated: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          },
          {
            id: "fallback_002",
            question: "Give an example of a verb.",
            options: ["Run", "Blue", "Quickly", "Happy"],
            answer: "Run",
            moreInfo: "Verbs are action words.",
            difficulty: 1,
            gradeLevel: 3,
            category: "parts_of_speech",
            tags: ["basic"],
            dateCreated: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          },
          {
            id: "fallback_003",
            question: "What is an adjective?",
            options: [
              "A person, place, or thing.",
              "A describing word.",
              "An action word.",
              "A conjunction."
            ],
            answer: "A describing word.",
            moreInfo: "Adjectives describe nouns.",
            difficulty: 1,
            gradeLevel: 3,
            category: "parts_of_speech",
            tags: ["basic"],
            dateCreated: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          }
        ]
      };
  
      this.categoriesDatabase = {
        version: "1.0.0-fallback",
        lastUpdated: new Date().toISOString(),
        categories: [
          {
            id: "parts_of_speech",
            name: "Parts of Speech",
            description: "Basic grammar building blocks",
            color: "#FF6B6B",
            icon: "🌱",
            gradeLevels: [3, 6, 9, 12]
          }
        ]
      };
  
      this.levelsDatabase = {
        version: "1.0.0-fallback",
        lastUpdated: new Date().toISOString(),
        levels: [
          {
            gradeLevel: 3,
            name: "Grammar Sprout",
            description: "Basic building blocks",
            icon: "🌱",
            color: "#FF6B6B",
            topics: ["Basic nouns", "Simple verbs"]
          },
          {
            gradeLevel: 6,
            name: "Word Builder",
            description: "Expanding knowledge",
            icon: "📚",
            color: "#4ECDC4",
            topics: ["Advanced grammar"]
          }
        ]
      };
  
      this.isLoaded = true;
    }
  
    // Core data access methods
    getAllCards(): GrammarCard[] {
      return this.cardsDatabase?.cards || [];
    }
  
    getCardsByGradeLevel(gradeLevel: number): GrammarCard[] {
      return this.getAllCards().filter(card => card.gradeLevel === gradeLevel);
    }
  
    getCardsByCategory(categoryId: string): GrammarCard[] {
      return this.getAllCards().filter(card => card.category === categoryId);
    }
  
    getCardsBySubcategory(subcategory: string): GrammarCard[] {
      return this.getAllCards().filter(card => card.subcategory === subcategory);
    }
  
    getCategories(): CardCategory[] {
      return this.categoriesDatabase?.categories || [];
    }
  
    getGradeLevels(): GradeLevel[] {
      return this.levelsDatabase?.levels || [];
    }
  
    getCategoriesForGrade(gradeLevel: number): CardCategory[] {
      return this.getCategories().filter(category => 
        category.gradeLevels.includes(gradeLevel)
      );
    }
  
    // Advanced filtering with performance optimization
    filterCards(options: FilterOptions): GrammarCard[] {
      let filteredCards = this.getAllCards();
  
      // Apply filters in order of selectivity for better performance
      if (options.gradeLevel) {
        filteredCards = filteredCards.filter(card => card.gradeLevel === options.gradeLevel);
      }
  
      if (options.category) {
        filteredCards = filteredCards.filter(card => card.category === options.category);
      }
  
      if (options.subcategory) {
        filteredCards = filteredCards.filter(card => card.subcategory === options.subcategory);
      }
  
      if (options.difficulty) {
        filteredCards = filteredCards.filter(card => card.difficulty === options.difficulty);
      }
  
      if (options.tags && options.tags.length > 0) {
        filteredCards = filteredCards.filter(card => 
          options.tags!.some(tag => card.tags.includes(tag))
        );
      }
  
      if (options.searchQuery) {
        const query = options.searchQuery.toLowerCase();
        filteredCards = filteredCards.filter(card =>
          card.question.toLowerCase().includes(query) ||
          card.answer.toLowerCase().includes(query) ||
          card.moreInfo.toLowerCase().includes(query) ||
          card.tags.some(tag => tag.toLowerCase().includes(query)) ||
          card.category.toLowerCase().includes(query) ||
          (card.subcategory && card.subcategory.toLowerCase().includes(query))
        );
      }
  
      // Accuracy-based filtering
      if (options.minAccuracy !== undefined || options.maxAccuracy !== undefined) {
        filteredCards = filteredCards.filter(card => {
          // FIX: Add proper null checks for statistics
          if (!card.statistics || !card.statistics.timesShown || card.statistics.timesShown === 0) {
            return true;
          }
          
          const accuracy = (card.statistics.correctAnswers / card.statistics.timesShown) * 100;
          
          if (options.minAccuracy !== undefined && accuracy < options.minAccuracy) return false;
          if (options.maxAccuracy !== undefined && accuracy > options.maxAccuracy) return false;
          
          return true;
        });
      }
  
      // Exclude recently seen cards
      if (options.excludeRecent) {
        const recentThreshold = new Date(Date.now() - 24 * 60 * 60 * 1000); // 24 hours ago
        filteredCards = filteredCards.filter(card => 
          !card.statistics?.lastShown || 
          new Date(card.statistics.lastShown) < recentThreshold
        );
      }
  
      return filteredCards;
    }
  
    // Utility methods
    getRandomCards(count: number, options?: FilterOptions): GrammarCard[] {
      const filteredCards = options ? this.filterCards(options) : this.getAllCards();
      const shuffled = [...filteredCards].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count);
    }
  
    searchCards(query: string): GrammarCard[] {
      return this.filterCards({ searchQuery: query });
    }
  
    getCardById(id: string): GrammarCard | undefined {
      return this.getAllCards().find(card => card.id === id);
    }
  
    // Statistics and analytics
    updateCardStatistics(cardId: string, isCorrect: boolean, responseTime: number): void {
      if (!this.cardsDatabase) return;
  
      const card = this.cardsDatabase.cards.find(c => c.id === cardId);
      if (!card) return;
  
      if (!card.statistics) {
        card.statistics = {
          timesShown: 0,
          correctAnswers: 0,
          incorrectAnswers: 0,
          averageResponseTime: 0,
          streak: 0
        };
      }
  
      const stats = card.statistics;
      stats.timesShown++;
      
      if (isCorrect) {
        stats.correctAnswers++;
        stats.streak = (stats.streak || 0) + 1;
      } else {
        stats.incorrectAnswers++;
        stats.streak = 0;
      }
  
      // Update response times
      const totalTime = stats.averageResponseTime * (stats.timesShown - 1) + responseTime;
      stats.averageResponseTime = Math.round(totalTime / stats.timesShown);
      
      if (!stats.fastestResponseTime || responseTime < stats.fastestResponseTime) {
        stats.fastestResponseTime = responseTime;
      }
      
      if (!stats.slowestResponseTime || responseTime > stats.slowestResponseTime) {
        stats.slowestResponseTime = responseTime;
      }
  
      stats.lastShown = new Date().toISOString();
  
      // Cache invalidation for analytics
      this.cache.delete('overall_stats');
      this.cache.delete('category_stats');
    }
  
    getCardAccuracy(cardId: string): number {
      const card = this.getCardById(cardId);
      // FIX: Add proper null checks for statistics
      if (!card?.statistics || !card.statistics.timesShown || card.statistics.timesShown === 0) {
        return 0;
      }
      
      return Math.round((card.statistics.correctAnswers / card.statistics.timesShown) * 100);
    }
  
    // Analytics with caching
    getOverallStats() {
      const cacheKey = 'overall_stats';
      const cached = this.cache.get(cacheKey);
      if (cached) return cached;
  
      const cards = this.getAllCards();
      const totalCards = cards.length;
      // FIX: Add proper null checks for statistics
      const cardsWithStats = cards.filter(card => 
        card.statistics?.timesShown && card.statistics.timesShown > 0
      );
      
      if (cardsWithStats.length === 0) {
        const stats = {
          totalCards,
          cardsPlayed: 0,
          averageAccuracy: 0,
          totalTimesPlayed: 0,
          averageResponseTime: 0,
          categories: this.getCategories().length,
          difficulties: new Set(cards.map(c => c.difficulty)).size
        };
        this.cache.set(cacheKey, stats);
        return stats;
      }
  
      const totalCorrect = cardsWithStats.reduce((sum, card) => 
        sum + (card.statistics?.correctAnswers || 0), 0
      );
      const totalPlayed = cardsWithStats.reduce((sum, card) => 
        sum + (card.statistics?.timesShown || 0), 0
      );
      const totalResponseTime = cardsWithStats.reduce((sum, card) => 
        sum + (card.statistics?.averageResponseTime || 0), 0
      );
  
      const stats = {
        totalCards,
        cardsPlayed: cardsWithStats.length,
        averageAccuracy: totalPlayed > 0 ? Math.round((totalCorrect / totalPlayed) * 100) : 0,
        totalTimesPlayed: totalPlayed,
        averageResponseTime: cardsWithStats.length > 0 ? Math.round(totalResponseTime / cardsWithStats.length) : 0,
        categories: this.getCategories().length,
        difficulties: new Set(cards.map(c => c.difficulty)).size
      };
  
      this.cache.set(cacheKey, stats);
      return stats;
    }
  
    getCategoryStats() {
      const cacheKey = 'category_stats';
      const cached = this.cache.get(cacheKey);
      if (cached) return cached;
  
      const categories = this.getCategories();
      const stats = categories.map(category => {
        const categoryCards = this.getCardsByCategory(category.id);
        // FIX: Add proper null checks for statistics
        const cardsWithStats = categoryCards.filter(card => 
          card.statistics?.timesShown && card.statistics.timesShown > 0
        );
        
        const totalCorrect = cardsWithStats.reduce((sum, card) => 
          sum + (card.statistics?.correctAnswers || 0), 0
        );
        const totalPlayed = cardsWithStats.reduce((sum, card) => 
          sum + (card.statistics?.timesShown || 0), 0
        );
  
        return {
          categoryId: category.id,
          categoryName: category.name,
          totalCards: categoryCards.length,
          cardsPlayed: cardsWithStats.length,
          accuracy: totalPlayed > 0 ? Math.round((totalCorrect / totalPlayed) * 100) : 0,
          timesPlayed: totalPlayed
        };
      });
  
      this.cache.set(cacheKey, stats);
      return stats;
    }
  
    // Adaptive learning recommendations
    getRecommendedCards(userLevel: number, weakCategories: string[] = [], count: number = 10): GrammarCard[] {
      const allCards = this.getAllCards();
      
      // Score cards based on various factors
      const scoredCards = allCards.map(card => {
        let score = 0;
        
        // Appropriate difficulty level (higher score for cards near user level)
        const levelDiff = Math.abs(card.difficulty - userLevel);
        score += Math.max(0, 5 - levelDiff);
        
        // Weak categories get priority
        if (weakCategories.includes(card.category)) {
          score += 10;
        }
        
        // Cards with low accuracy get priority
        if (card.statistics?.timesShown && card.statistics.timesShown > 0) {
          const accuracy = card.statistics.correctAnswers / card.statistics.timesShown;
          if (accuracy < 0.7) score += 8;
          if (accuracy < 0.5) score += 5;
          
          // Less recently seen cards get priority
          if (card.statistics.lastShown) {
            const daysSinceLastSeen = (Date.now() - new Date(card.statistics.lastShown).getTime()) / (1000 * 60 * 60 * 24);
            score += Math.min(5, daysSinceLastSeen);
          }
        } else {
          // New cards get moderate priority
          score += 6;
        }
        
        // Add some randomness to prevent always getting the same cards
        score += Math.random() * 3;
        
        return { card, score };
      });
      
      // Sort by score and return top cards
      return scoredCards
        .sort((a, b) => b.score - a.score)
        .slice(0, count)
        .map(item => item.card);
    }
  
    // Study session management
    createStudySession(gradeLevel: number, categories: string[]): StudySession {
      return {
        id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        startTime: new Date().toISOString(),
        cardsStudied: [],
        correctAnswers: 0,
        totalAnswers: 0,
        gradeLevel,
        categories,
        averageResponseTime: 0
      };
    }
  
    updateStudySession(session: StudySession, cardId: string, isCorrect: boolean, responseTime: number): void {
      session.cardsStudied.push(cardId);
      session.totalAnswers++;
      if (isCorrect) session.correctAnswers++;
      
      // Update average response time
      const totalTime = session.averageResponseTime * (session.totalAnswers - 1) + responseTime;
      session.averageResponseTime = Math.round(totalTime / session.totalAnswers);
    }
  
    endStudySession(session: StudySession): StudySession {
      session.endTime = new Date().toISOString();
      return session;
    }
  
    // Data management
    clearCache(): void {
      this.cache.clear();
    }
  
    getMetadata() {
      return this.cardsDatabase?.metadata || null;
    }
  
    isReady(): boolean {
      return this.isLoaded && !!this.cardsDatabase && !!this.categoriesDatabase && !!this.levelsDatabase;
    }
  
    // CSV Export functionality
    exportCardsToCSV(): string {
      const cards = this.getAllCards();
      const headers = ['ID', 'Question', 'Option 1', 'Option 2', 'Option 3', 'Option 4', 'Answer', 'Explanation', 'Difficulty', 'Grade Level', 'Category', 'Subcategory', 'Tags'];
      
      const csvRows = [
        headers.join(','),
        ...cards.map(card => [
          card.id,
          `"${card.question.replace(/"/g, '""')}"`,
          `"${card.options[0]?.replace(/"/g, '""') || ''}"`,
          `"${card.options[1]?.replace(/"/g, '""') || ''}"`,
          `"${card.options[2]?.replace(/"/g, '""') || ''}"`,
          `"${card.options[3]?.replace(/"/g, '""') || ''}"`,
          `"${card.answer.replace(/"/g, '""')}"`,
          `"${card.moreInfo.replace(/"/g, '""')}"`,
          card.difficulty,
          card.gradeLevel,
          card.category,
          card.subcategory || '',
          `"${card.tags.join(';')}"`
        ].join(','))
      ];
      
      return csvRows.join('\n');
    }
  }